import { useAuth0 } from '@auth0/auth0-react';
import { getDownloadURL, getStorage, ref, uploadString } from 'firebase/storage';
import { firebaseApp } from 'firebaseConfig';
import { useCallback } from 'react';
import API from 'api/API';
import { useForceStoragePassthroughForLocation } from 'api/hooks/useForceStoragePassthroughForLocation';
import { UnscannableImplantPathParams } from 'api/models/Implants';
import { SadlSet } from 'api/models/Sets';
import { TrayImagePathParams } from 'api/models/Trays';
import useForceStoragePassthrough from 'hooks/useForceStoragePassthrough';

const useGoogleStorageHelpers = () => {
  const { getAccessTokenSilently } = useAuth0();

  const { forceStoragePassthroughForLocation } = useForceStoragePassthroughForLocation();
  const forceStoragePassthrough = useForceStoragePassthrough()[0];
  const gcpNetworkBlocked =
    forceStoragePassthroughForLocation || forceStoragePassthrough || !!localStorage.getItem('gcpNetworkBlocked');

  const saviApiUrl = process.env.REACT_APP_SAVI_API_HOST + '/api/v2/hospital/deliveries/';

  /**
   * Constructs a full filepath to the location captured tray images are stored
   *
   * @param param0
   * @returns ${TRAY_IMAGE_UPLOAD_BUCKET}/orders/${orderId}/${orderStage}/TrayPhotos/${setCode}/${setInstanceId}/${trayId}-photo.png
   */
  const constructTrayImagePath = useCallback(
    ({ stage, deliveryId, setId, trayId, decantedTrayId }: TrayImagePathParams): string => {
      return `${
        process.env.REACT_APP_SAVI_TRAY_IMAGE_UPLOAD_BUCKET
      }/orders/${deliveryId}/${stage}/TrayPhotos/${setId}/${trayId}${
        decantedTrayId ? `-${decantedTrayId}` : ''
      }-photo.jpg`;
    },
    [],
  );

  /**
   * Constructs a full filepath to the location captured tray images are stored
   *
   * @param param0
   * @returns ${TRAY_IMAGE_UPLOAD_BUCKET}/orders/${orderId}/${orderStage}/TrayPhotos/${setCode}/${setInstanceId}/${trayId}-photo.png
   */
  const constructNonscannablelImplantPath = useCallback(
    ({ stage, deliveryId }: UnscannableImplantPathParams): string => {
      return `${process.env.REACT_APP_SAVI_TRAY_IMAGE_UPLOAD_BUCKET}/orders/${deliveryId}/${stage}/NonScannableImplantImages/unscannable-image-photo.jpg`;
    },
    [],
  );

  const uploadImage = useCallback(
    async (imagePath: string, dataUri: string, metadata?: Record<string, string>): Promise<string | undefined> => {
      // if GCP network is blocked, upload via SAVI API
      if (gcpNetworkBlocked) {
        const gsProtoStr = 'gs://';
        const protoStr = imagePath.slice(0, 5) == gsProtoStr ? '' : gsProtoStr;
        await API.deliveries.uploadGcpFile(protoStr + imagePath, dataUri, metadata);
      }

      // else upload via GCP
      else {
        const storageRef = ref(getStorage(firebaseApp), imagePath);
        await uploadString(storageRef, dataUri, 'data_url', metadata ? { customMetadata: metadata } : undefined);
      }

      return imagePath;
    },
    [gcpNetworkBlocked],
  );

  const getDownloadUrlFromGS = useCallback(
    async (gs: string): Promise<string> => {
      // when GCP network is blocked, get from SAVI API
      if (gcpNetworkBlocked) {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams({ gs, token, ts: new Date().getTime().toString() });
        return saviApiUrl + 'storage/download-file/?' + params;
      }

      // get from CGP
      return await getDownloadURL(ref(getStorage(firebaseApp), gs));
    },
    [gcpNetworkBlocked, getAccessTokenSilently, saviApiUrl],
  );

  // makes path with format:
  //  gs://bucket-name/tenant-name/tray_reference_images/ZZINAUK0001_REV8.0_TRAY_TWO.png
  const getGsImagePathFromSaldSet = useCallback((set: SadlSet, trayReference: string): string => {
    const setRevision = set.revision.replace(/\s/g, '');
    const trayRef = trayReference.replace(/\s/g, '_');
    return `${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${set.tenant}/tray_reference_images/${set.code}_${setRevision}_${trayRef}.png`;
  }, []);

  return {
    getDownloadUrlFromGS,
    getGsImagePathFromSaldSet,
    constructTrayImagePath,
    constructNonscannablelImplantPath,
    uploadImage,
    gcpNetworkBlocked,
  };
};

export default useGoogleStorageHelpers;
